import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { agentsService } from '../../services/agents.service';
import { countriesService } from '../../services/countries.service';
import { statesService } from '../../services/states.service';
import { citiesService } from '../../services/cities.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';

const GRAD = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const initialAgent = {
  password: "",
  company_name: "",
  executive: "",
  mobile: "",
  email: "",
  address: "",
  pincode: "",
  city: "",
  state: "",
  country: "",
  status: "active",
};

const AddAgent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const editAgentId = location.state?.agentId;
  const isEdit = !!editAgentId;

  const [loading, setLoading] = useState(false);
  const [agent, setAgent] = useState(initialAgent);
  // option values are names (the agent API stores names); id is kept to bind the dependent dropdowns
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  useEffect(() => {
    (async () => {
      const countries = await loadCountryOptions();
      if (isEdit) loadAgentDetail(countries);
    })();
  }, [editAgentId]);

  const toOptions = (list) => (list || []).map(i => ({ value: i.name, label: i.name, id: i.id }));

  const loadCountryOptions = async () => {
    try {
      const res = await countriesService.list({ page: 1, status: "active" });
      const opts = toOptions(res?.resData?.data);
      setCountryOptions(opts);
      return opts;
    } catch (err) {
      setCountryOptions([]);
      return [];
    }
  }

  const loadStateOptions = async (countryId) => {
    if (!countryId) { setStateOptions([]); return []; }
    try {
      const res = await statesService.list({ page: 1, status: "active", country_id: countryId });
      const opts = toOptions(res?.resData?.data);
      setStateOptions(opts);
      return opts;
    } catch (err) {
      setStateOptions([]);
      return [];
    }
  }

  const loadCityOptions = async (stateId) => {
    if (!stateId) { setCityOptions([]); return []; }
    try {
      const res = await citiesService.list({ page: 1, status: "active", state_id: stateId });
      const opts = toOptions(res?.resData?.data);
      setCityOptions(opts);
      return opts;
    } catch (err) {
      setCityOptions([]);
      return [];
    }
  }

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const loadAgentDetail = async (countries) => {
    try {
      setLoading(true);
      const res = await agentsService.details(editAgentId);
      if (res?.success) {
        const v = res.resData?.agent ?? res.resData;
        setAgent({
          password: "",
          company_name: v?.company_name || "",
          executive: v?.executive || "",
          mobile: v?.mobile || "",
          email: v?.email || "",
          address: v?.address || "",
          pincode: v?.pincode || "",
          city: v?.city || "",
          state: v?.state || "",
          country: v?.country || "",
          status: v?.status || "active",
        });
        const country = countries.find(c => c.value === v?.country);
        const states = await loadStateOptions(country?.id);
        const state = states.find(st => st.value === v?.state);
        await loadCityOptions(state?.id);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (e) => {
    setAgent({ ...agent, [e.target.name]: e.target.value });
  }

  const handleMobileChange = (e) => {
    setAgent({ ...agent, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) });
  }

  const handlePincodeChange = (e) => {
    setAgent({ ...agent, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) });
  }

  const handleCountryChange = (o) => {
    setAgent({ ...agent, country: o ? o.value : "", state: "", city: "" });
    setCityOptions([]);
    loadStateOptions(o?.id);
  }

  const handleStateChange = (o) => {
    setAgent({ ...agent, state: o ? o.value : "", city: "" });
    loadCityOptions(o?.id);
  }

  const validate = () => {
    if (!agent.company_name.trim()) return "Company name is required";
    if (!agent.executive.trim()) return "Executive name is required";
    if ((!isEdit || agent.password) && agent.password.length < 6) return "Password must be at least 6 characters";
    if (!/^\d{10}$/.test(agent.mobile)) return "Enter a valid 10 digit mobile number";
    if (!/^\S+@\S+\.\S+$/.test(agent.email)) return "Enter a valid email address";
    if (!agent.address.trim()) return "Address is required";
    if (!/^\d{6}$/.test(agent.pincode)) return "Enter a valid 6 digit pincode";
    if (!agent.city.trim() || !agent.state.trim() || !agent.country.trim()) return "Country, state and city are required";
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: isEdit ? "Update agent?" : "Save agent?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Yes, update" : "Yes, save",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      const { password, ...rest } = agent;
      const res = isEdit
        ? await agentsService.update(editAgentId, { ...rest, ...(password ? { password } : {}) })
        : await agentsService.create({ ...rest, password });
      if (res?.success) {
        await withReactContent(Swal).fire({ title: "Success", text: res.message, icon: "success" });
        if (isEdit) {
          navigate("/agency/list");
        } else {
          setAgent(initialAgent);
          setStateOptions([]);
          setCityOptions([]);
        }
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (<ContentLoader loaderType="circleSpin" />)

  const labelStyle = { fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "#6c757d", marginBottom: 6 };
  const inputStyle = { borderRadius: 8, border: "1px solid #dfe3ec", fontSize: 13, height: 40, background: "#fff" };
  const readOnlyStyle = { ...inputStyle, background: "#eef0f8", color: "#4a5568" };
  const selectStyles = {
    control: (base, state) => ({
      ...base, borderRadius: 8, border: state.isFocused ? "1px solid #667eea" : "1px solid #dfe3ec", minHeight: 40,
      fontSize: 13, boxShadow: state.isFocused ? "0 0 0 3px rgba(102,126,234,.15)" : "none", "&:hover": { borderColor: "#667eea" }
    }),
    option: (base, state) => ({
      ...base, fontSize: 13,
      background: state.isSelected ? "#667eea" : state.isFocused ? "#eef0ff" : "#fff",
      color: state.isSelected ? "#fff" : "#1f2a44",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 10px" }),
    placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  };
  const sectionCard = { background: "#fff", borderRadius: 12, border: "1px solid #e8ebf7", padding: 20, marginBottom: 16 };
  const sectionTitle = (icon, text) => (
    <h6 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ fontSize: 14, color: "#4b3f8f" }}>
      <i className={"bi " + icon} style={{ color: "#667eea" }}></i> {text}
    </h6>
  );
  const field = (label, required, node, cls = "col-12 col-md-6 col-lg-4") => (
    <div className={cls}>
      <div style={labelStyle}>{label}{required && <span className="text-danger"> *</span>}</div>
      {node}
    </div>
  );
  const title = isEdit ? "Update Agent" : "Add Agent";

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "#1a202c", fontSize: 20 }}>{isEdit ? "Update Agent" : "Add New Agent"}</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#8a9ab5" }}>
              <Link to="/" style={{ color: "#8a9ab5" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/setting" style={{ color: "#8a9ab5" }}>Setting</Link>   
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>           
              <Link to="/agency/list" style={{ color: "#8a9ab5" }}>Agency List</Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>{title}</span>
            </nav>
          </div>
          <Link to="/agency/list" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "#4f3df5", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-list-ul"></i> Agency List
          </Link>
        </div>

        <form onSubmit={handleSubmit}>

            <div style={sectionCard}>
              {sectionTitle("bi-building", "Company Detail")}
              <div className="row g-3">
                {field("Company Name", true,
                  <input type="text" className="form-control" placeholder="e.g. Sky Travels Pvt Ltd" name="company_name"
                    value={agent.company_name} onChange={handleChange} style={inputStyle} />
                )}
                {field(isEdit ? "New Password" : "Password", !isEdit,
                  <input type="password" autoComplete="new-password" className="form-control" placeholder={isEdit ? "Leave blank to keep current" : "Minimum 6 characters"} name="password"
                    value={agent.password} onChange={handleChange} style={inputStyle} />
                )}
                {field("Status", false,
                  <Select
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find(o => o.value === agent.status) || null}
                    onChange={(o) => setAgent({ ...agent, status: o ? o.value : "active" })}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
              </div>
            </div>

            <div style={sectionCard}>
              {sectionTitle("bi-person-lines-fill", "Contact Detail")}
              <div className="row g-3">
                {field("Executive", true,
                  <input type="text" className="form-control" placeholder="Contact person name" name="executive"
                    value={agent.executive} onChange={handleChange} style={inputStyle} />
                )}
                {field("Mobile", true,
                  <input type="text" inputMode="numeric" className="form-control" placeholder="10 digit mobile number" name="mobile"
                    value={agent.mobile} onChange={handleMobileChange} style={inputStyle} />
                )}
                {field("Email", true,
                  <input type="email" className="form-control" placeholder="name@company.com" name="email"
                    value={agent.email} onChange={handleChange} style={inputStyle} />
                )}
              </div>
            </div>

            <div style={sectionCard}>
              {sectionTitle("bi-geo-alt", "Address Detail")}
              <div className="row g-3">
                {field("Address", true,
                  <input type="text" className="form-control" placeholder="Street, area, landmark" name="address"
                    value={agent.address} onChange={handleChange} style={inputStyle} />,
                  "col-12"
                )}
                {field("Country", true,
                  <Select
                    options={countryOptions}
                    value={countryOptions.find(o => o.value === agent.country) || null}
                    onChange={handleCountryChange}
                    placeholder="Select country"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("State", true,
                  <Select
                    options={stateOptions}
                    value={stateOptions.find(o => o.value === agent.state) || null}
                    onChange={handleStateChange}
                    placeholder="Select state"
                    isClearable
                    isDisabled={!agent.country}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("City", true,
                  <Select
                    options={cityOptions}
                    value={cityOptions.find(o => o.value === agent.city) || null}
                    onChange={(o) => setAgent({ ...agent, city: o ? o.value : "" })}
                    placeholder="Select city"
                    isClearable
                    isDisabled={!agent.state}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Pincode", true,
                  <input type="text" inputMode="numeric" className="form-control" placeholder="6 digit pincode" name="pincode"
                    value={agent.pincode} onChange={handlePincodeChange} style={inputStyle} />
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <Link to="/agency/list" className="btn px-4 d-flex align-items-center"
                style={{ background: "#fff", border: "1px solid #dfe3ec", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}>
                Back
              </Link>
              <button type="submit" className="btn px-4 text-white fw-semibold"
                style={{ background: GRAD, border: "none", borderRadius: 8, height: 40, fontSize: 13 }}>
                {title}
              </button>
            </div>

        </form>
      </section>
    </>
  );
}

export default AddAgent;
